import React, { useEffect, useState } from "react";
import withLayout from "../../../hocs/Layout";
import { apiConstants, postGeneric, postMultipart } from "../../../common/constants";
import { FormProvider, RHFFileField } from "../../../common/hook-form";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { Box, Button, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageTitle from "../../../common/PageTitle";
import { useSnackbar } from "notistack";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import DownloadSampleFile from "../../../common/DownloadSampleFile";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import { formatDate } from "../../../helpers/UtilityHelper";
import Loader from "../../../common/Loader";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR } from '../../../styles/foundation/colors';
import {
    pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx,
    uploadSectionSx, primaryBtnSx, helpTextSx, helpTextTypeSx,
    dataGridWrapSx, btnRowSx,
} from '../../../styles/patterns';

const DonwloadMISfromAWB = (props) => {
    const shipmentType = props.shipmentType;
    const { enqueueSnackbar } = useSnackbar();
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : '';
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [data, setData] = useState([]);
    const [reload, setReload] = useState(0);

    const methodsBulkUpload = useForm({
        resolver: yupResolver(Yup.object().shape({ orderFile: Yup.mixed().required("Please Select a File") })),
        defaultValues: { orderFile: '' }
    });
    const { handleSubmit: handleSubmitBulkUpload, reset: resetForm } = methodsBulkUpload;

    const onSubmitBulkUpload = async (data) => {
        setIsUploadingFile(true);
        try {
            const formData = new FormData();
            formData.append("excelFile", data.orderFile[0]);
            const response = await postMultipart(`${apiConstants.GET_MIS_FROM_AWB}?merchantOid=${merchantId}`, formData);
            setReload(prev => prev + 1);
            enqueueSnackbar(response.status === 200 ? "Successfully Uploaded!" : response.data, {
                variant: response.status === 200 ? "success" : "error"
            });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Error uploading file", { variant: "error" });
        } finally {
            setIsUploadingFile(false);
            resetForm();
        }
    };

    useEffect(() => {
        setIsLoading(true);
        const request = {
            collection: "bulk_orders_result",
            offset: page * sizePerPage, limit: sizePerPage,
            andfilter: [
                { field: "type", operator: "eq", value: 'AWB_NUMBER_BULK_ORDER_REPORT' },
                { field: "merchantId", operator: "eq", value: merchant?.merchantId }
            ],
            sort: { field: "time", direction: "DESC" }
        };
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, request)
            .then((res) => setData(res?.data?.data))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizePerPage, page, reload, shipmentType]);

    const columns = [
        { field: '_id', headerAlign: "left", hideSortIcons: true, headerName: 'Id', flex: 1, renderCell: (p) => p.row?._id },
        { field: 'file', headerAlign: "left", hideSortIcons: true, headerName: 'File Name', flex: 1.2, renderCell: (p) => p.row?.fileName },
        { field: 'modifiedByUser', headerAlign: "left", hideSortIcons: true, headerName: 'Uploaded By', flex: 1.2, renderCell: (p) => p.row?.modifiedByUser },
        {
            field: 'status', headerAlign: "left", hideSortIcons: true, headerName: 'Status', flex: 0.8,
            renderCell: (p) => {
                const ok = p.row?.status === 'COMPLETED';
                return (
                    <Typography sx={{
                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                        color: ok ? SUCCESS : ERROR, bgcolor: (ok ? SUCCESS : ERROR) + '14',
                        px: 1, py: 0.25, borderRadius: '6px', display: 'inline-block',
                    }}>
                        {ok ? 'Completed' : 'Stuck / Failed'}
                    </Typography>
                );
            },
        },
        { field: 'type', headerAlign: "left", hideSortIcons: true, headerName: 'Type', flex: 0.6, renderCell: (p) => p.row?.type },
        { field: 'time', headerAlign: "left", hideSortIcons: true, headerName: 'Upload Date', flex: 1, renderCell: (p) => p.row?.time ? formatDate(p.row.time) : '' },
        { field: 'totalOrders', headerAlign: "left", hideSortIcons: true, headerName: 'Total', flex: 0.8, renderCell: (p) => p.row?.totalOrders },
        { field: 'processed', headerAlign: "left", hideSortIcons: true, headerName: 'Processed', flex: 0.8, renderCell: (p) => p.row?.processed },
        {
            field: 'reportS3Url', headerAlign: "left", hideSortIcons: true, headerName: 'Report', flex: 1,
            renderCell: (p) => (
                <Button size="small" sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE }}
                    onClick={() => window.open(p.row?.reportS3Url, '_blank')} startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}>
                    Download
                </Button>
            ),
        },
    ];

    return (
        <Box sx={pageContainerSx}>
            {isLoading && <Loader />}
            <PageTitle subTitle={"Upload an XLSX file with AWB numbers to generate MIS reports"}>Download MIS from AWB</PageTitle>
{/* 
            <Box sx={pageHeroSx}>
                <Typography sx={heroTitleSx}>MIS Report from AWB</Typography>
                <Typography sx={heroSubSx}>Upload an XLSX file with AWB numbers to generate MIS reports</Typography>
            </Box> */}

            <Box sx={uploadSectionSx}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5 }}>
                    Upload AWB File
                </Typography>
                {/* <Box sx={helpTextSx}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#3783e7', mt: 0.2 }} />
                    <Typography sx={helpTextTypeSx}>
                        <strong>How it works:</strong> Upload an XLSX file containing AWB numbers.
                        The MIS report will be processed in the background and appear in the upload history below.
                    </Typography>
                </Box> */}

                <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
                    <RHFFileField name="orderFile" fileType={["xlsx"]} required={true} />
                    <Box sx={{ ...btnRowSx, mt: 2 }}>
                        <LoadingButton startIcon={<FileUploadIcon />} type="submit" loading={isUploadingFile}
                            variant="contained" size="small" sx={primaryBtnSx}>
                            Upload
                        </LoadingButton>
                        <DownloadSampleFile fileName={"AWB_MIS_REPORT"} label='Download Sample' excel={'excel'} />
                    </Box>
                </FormProvider>
            </Box>

            <Box sx={dataGridWrapSx}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>Upload History</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3 }}>Track MIS report generation status and download completed reports</Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                    <ProzoDataGrid
                        columns={columns} rows={data || []}
                        sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
                        setPage={setPage} page={page} pagination={true}
                        hideFooterPagination={false} filterMode={"server"}
                        rowCount={Number.MAX_VALUE} hideCountNumber={true}
                        getRowId={(row) => row._id}
                    />
                </Box>
            </Box>
        </Box>
    );
};
export default withLayout(DonwloadMISfromAWB);
